import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { DateRange } from 'react-day-picker';

// Mask input for MM-DD-YYYY HH:mm
const applyMask = (value: string) => {
  let clean = value.replace(/\D/g, '');
  const clamp = (val: string, min: number, max: number, pad = 2) => {
    let n = parseInt(val);
    if (isNaN(n)) n = min;
    n = Math.max(min, Math.min(max, n));
    return n.toString().padStart(pad, '0');
  };
  // Month
  if (clean.length === 1 && parseInt(clean) > 1) clean = '0' + clean;
  if (clean.length >= 2) clean = clamp(clean.slice(0, 2), 1, 12) + clean.slice(2);
  // Day
  if (clean.length >= 4) clean = clean.slice(0, 2) + clamp(clean.slice(2, 4), 1, 31) + clean.slice(4);
  // Hour
  if (clean.length >= 10) clean = clean.slice(0, 8) + clamp(clean.slice(8, 10), 0, 23) + clean.slice(10);
  // Minute
  if (clean.length >= 12) clean = clean.slice(0, 10) + clamp(clean.slice(10, 12), 0, 59) + clean.slice(12);
  const digits = clean.slice(0, 12);
  let res = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) res += '-';
    else if (i === 8) res += ' ';
    else if (i === 10) res += ':';
    res += digits[i];
  }
  return res;
}

export const calculateRange = (rawVal: string, ianaTimezone: string, minDate?: Date, olderThan90DaysMode = false): DateRange | undefined => {

  const normMatch = rawVal.match(/^last[_ ](\d+)[_ ](minutes?|mins?|hours?|hrs?|days?)$/i);
  const val = normMatch
    ? `${normMatch[1]}${normMatch[2].toLowerCase().startsWith('min') ? 'm' : normMatch[2].toLowerCase().startsWith('h') ? 'h' : 'd'}`
    : rawVal;

  let from: Date | undefined;
  let to: Date = moment.tz(ianaTimezone).toDate();

  const parseValue = (v: string) => {
    const match = v.match(/^(\d+)\s*(m|min|minutes|h|hr|hour|d|days|day|w|weeks|week)$/i);
    if (!match) return null;
    const num = parseInt(match[1]);
    const unit = match[2].toLowerCase();

    if (unit.startsWith('m') && !unit.startsWith('mi')) return { num, unit: 'minutes' }; // handle 'm' as minutes
    if (unit.startsWith('mi')) return { num, unit: 'minutes' };
    if (unit.startsWith('h')) return { num, unit: 'hours' };
    if (unit.startsWith('d')) return { num, unit: 'days' };
    if (unit.startsWith('w')) return { num, unit: 'weeks' };
    return null;
  };

  const dynamic = parseValue(val);

  if (dynamic) {
    from = moment.tz(ianaTimezone).subtract(dynamic.num, dynamic.unit as any).toDate();
    if (dynamic.unit === 'weeks') {
      from = moment(from).startOf('day').toDate();
    }
  } else {
    switch (val) {
      case '5m':
        from = moment.tz(ianaTimezone).subtract(5, 'minutes').toDate();
        break;
      case '10m':
        from = moment.tz(ianaTimezone).subtract(10, 'minutes').toDate();
        break;
      case '15m':
        from = moment.tz(ianaTimezone).subtract(15, 'minutes').toDate();
        break;
      case '30m':
        from = moment.tz(ianaTimezone).subtract(30, 'minutes').toDate();
        break;
      case '1h':
        from = moment.tz(ianaTimezone).subtract(1, 'hours').toDate();
        break;
      case 'today':
        from = moment.tz(ianaTimezone).startOf('day').toDate();
        break;
      case 'yesterday':
        from = moment.tz(ianaTimezone).subtract(1, 'day').startOf('day').toDate();
        to = moment.tz(ianaTimezone).subtract(1, 'day').endOf('day').toDate();
        break;
      case '24h':
        from = moment.tz(ianaTimezone).subtract(1, 'days').toDate();
        break;
      case '7d':
        from = moment.tz(ianaTimezone).subtract(7, 'days').toDate();
        break;
      case '30d':
        from = moment.tz(ianaTimezone).subtract(30, 'days').toDate();
        break;
      case '90d':
        from = moment.tz(ianaTimezone).subtract(90, 'days').toDate();
        break;
      case 'this_month':
        from = moment.tz(ianaTimezone).startOf('month').toDate();
        break;
      case 'last_month':
        from = moment.tz(ianaTimezone).subtract(1, 'month').startOf('month').toDate();
        to = moment.tz(ianaTimezone).subtract(1, 'month').endOf('month').toDate();
        break;
      case 'last_3_months':
        from = moment.tz(ianaTimezone).subtract(3, 'months').toDate();
        break;
      case 'this_year':
        from = moment.tz(ianaTimezone).startOf('year').toDate();
        break;
      case 'last_year':
        from = moment.tz(ianaTimezone).subtract(1, 'year').startOf('year').toDate();
        to = moment.tz(ianaTimezone).subtract(1, 'year').endOf('year').toDate();
        break;
      case 'last_2_years':
        from = moment.tz(ianaTimezone).subtract(2, 'years').toDate();
        break;
      case 'more_than_90_days': {
        from = olderThan90DaysMode
          ? undefined
          : (minDate
            ? moment.tz(minDate, ianaTimezone).startOf('day').toDate()
            : moment.tz(ianaTimezone).subtract(365, 'days').startOf('day').toDate());

        to = moment.tz(ianaTimezone).subtract(90, 'days').endOf('day').toDate();
        break;
      }
      default:
        return undefined;
    }
  }

  if (minDate && from && moment.tz(from, ianaTimezone).isBefore(moment.tz(minDate, ianaTimezone), 'day')) {
    from = moment.tz(ianaTimezone).year(minDate.getFullYear()).month(minDate.getMonth()).date(minDate.getDate()).startOf('day').toDate();
  }

  return { from, to };
};

export const toCalendarDate = (date: Date, ianaTimezone: string): Date => {
  const m = moment.tz(date, ianaTimezone);
  return new Date(m.year(), m.month(), m.date());
}

export const formatMomentDate = (ianaTimezone: string, date?: Date) =>
  date ? moment(date).tz(ianaTimezone).format('MM-DD-YYYY HH:mm') : '';

interface UseDateTimeRangePickerProps {
  initialRange?: 'today' | '24h' | '7d' | '30d' | 'custom' | string;
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  ianaTimezone: string;
  minDate?: Date;
  olderThan90DaysMode?: boolean;
}

export const useDateTimeRangePicker = ({
  initialRange = 'today',
  initialStartDate,
  initialEndDate,
  ianaTimezone = 'America/New_York',
  minDate,
  olderThan90DaysMode = false,
}: UseDateTimeRangePickerProps) => {
  const [selectedRange, setSelectedRange] = useState(initialRange);
  const [range, setRange] = useState<DateRange | undefined>(() => {
    if (initialStartDate && initialEndDate) {
      let from = initialStartDate;
      if (minDate && moment(from).isBefore(minDate, 'day')) {
        from = moment(minDate).startOf('day').toDate();
      }
      return { from, to: initialEndDate };
    }
    return undefined;
  });

  const [month, setMonth] = useState<Date>(
    initialEndDate || moment.tz(ianaTimezone).subtract(1, 'day').toDate()
  );

  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');

  const isCustom = selectedRange === 'custom';

  const canNavigate = (type: 'prev' | 'next') => {
    if (isCustom) return true;

    const current = moment(month).startOf('month');
    const start = range?.from && moment(range.from).startOf('month');
    const end = range?.to && moment(range.to).startOf('month');

    if (type === 'prev') return start ? current.isAfter(start) : false;
    return end ? current.isBefore(end) : false;
  };

  const changeMonth = (dir: 'prev' | 'next') => {
    if (!canNavigate(dir)) return;

    setMonth(prev =>
      moment(prev)[dir === 'prev' ? 'subtract' : 'add'](1, 'month').toDate()
    );
  };

  useEffect(() => {
    if (range?.from) setStartDateInput(formatMomentDate(ianaTimezone, range.from));
    if (range?.to) setEndDateInput(formatMomentDate(ianaTimezone, range.to));
  }, []);

  useEffect(() => {
    if (initialRange === 'custom') return;

    const calculated = calculateRange(initialRange, ianaTimezone, minDate, olderThan90DaysMode);
    if (!calculated) return;

    setRange(calculated);
    setStartDateInput(format(ianaTimezone, calculated.from));
    setEndDateInput(format(ianaTimezone, calculated.to));

    if (calculated.to) setMonth(toCalendarDate(calculated.to, ianaTimezone));
  }, []);

  const handleFixedRange = (val: string) => {
    const newRange = calculateRange(val, ianaTimezone, minDate, olderThan90DaysMode);

    setSelectedRange(val);
    setRange(newRange);
    setStartDateInput(format(ianaTimezone, newRange?.from));
    setEndDateInput(format(ianaTimezone, newRange?.to));

    if (newRange?.to) setMonth(toCalendarDate(newRange.to, ianaTimezone));
  };

  const handleCustomRange = () => {
    setSelectedRange('custom');
    setRange(undefined);
    setStartDateInput('');
    setEndDateInput('');
    setMonth(toCalendarDate(moment.tz(ianaTimezone).toDate(), ianaTimezone));
  };

  const handleDateChange =
    (type: 'from' | 'to') =>
      (rawVal: string) => {
        const val = applyMask(rawVal);
        if (type === 'from') setStartDateInput(val);
        else setEndDateInput(val);
        if (!val) {
          setRange(prev => ({
            from: type === 'from' ? undefined : prev?.from,
            to: type === 'to' ? undefined : prev?.to,
          }));
          return;
        }

        const parsed = moment.tz(val, ['MM-DD-YYYY HH:mm', 'MM-DD-YYYY'], ianaTimezone);
        if (!parsed.isValid()) return;

        let date = parsed.toDate();
        if (minDate && moment(date).isBefore(minDate, 'day')) {
          date = moment(minDate).startOf('day').toDate();
          if (type === 'from') setStartDateInput(format(ianaTimezone, date));
          else setEndDateInput(format(ianaTimezone, date));
        }

        setRange(prev => ({
          from: type === 'from' ? date : prev?.from,
          to: type === 'to' ? date : prev?.to,
        }));

        setMonth(toCalendarDate(date, ianaTimezone));
      };

  const displayRange = useMemo(() => {
    if (!range?.from) return;

    const from = toCalendarDate(range.from, ianaTimezone);
    const to = range.to ? toCalendarDate(range.to, ianaTimezone) : from;

    return { from, to };
  }, [range]);

  const format = (ianaTimezone: string, date?: Date) =>
    date ? moment(date).tz(ianaTimezone).format('MM-DD-YYYY HH:mm') : '';

  return {
    selectedRange,
    setSelectedRange,
    range,
    setRange,
    month,
    setMonth,
    startDateInput,
    endDateInput,
    setStartDateInput,
    setEndDateInput,
    isCustom,
    displayRange,
    canGoPrev: canNavigate('prev'),
    canGoNext: canNavigate('next'),
    changeMonth,
    handleFixedRange,
    handleCustomRange,
    handleDateChange,
  };
};
